import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';

const ADMIN_NOTIFICATIONS_TAG = 'AdminNotifications';

// Types
interface OutboundNotification {
  id?: string;
  name: string;
  setting_key: string;
  find_function: string;
  send_function: string;
  cadence: string;
}

interface CreateOutboundNotificationParams {
  name: string;
  setting_key: string;
  find_function: string;
  send_function: string;
  cadence: string;
}

interface UpdateOutboundNotificationParams {
  id: string;
  name: string;
  setting_key: string;
  find_function: string;
  send_function: string;
  cadence: string;
}

/**
 * RTK Query API for admin notification configuration endpoints
 */
export const adminNotificationConfigApi = createApi({
  reducerPath: 'adminNotificationConfig',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ADMIN_NOTIFICATIONS_TAG],
  endpoints: (builder) => ({
    /**
     * Query: Get all outbound notifications
     */
    getAllOutboundNotifications: builder.query<OutboundNotification[], void>({
      query: () => ({
        url: `/v1/admin/outbound-notifications`,
        method: 'GET',
      }),
      providesTags: [{ type: ADMIN_NOTIFICATIONS_TAG, id: 'LIST' }],
    }),

    /**
     * Mutation: Create a new outbound notification
     */
    createOutboundNotification: builder.mutation<OutboundNotification, CreateOutboundNotificationParams>({
      query: ({ name, setting_key, find_function, send_function, cadence }) => ({
        url: `/v1/admin/outbound-notifications`,
        method: 'POST',
        body: {
          name,
          setting_key,
          find_function,
          send_function,
          cadence,
        },
      }),
      invalidatesTags: [{ type: ADMIN_NOTIFICATIONS_TAG, id: 'LIST' }],
    }),

    /**
     * Mutation: Update an existing outbound notification
     */
    updateOutboundNotification: builder.mutation<OutboundNotification, UpdateOutboundNotificationParams>({
      query: ({ id, name, setting_key, find_function, send_function, cadence }) => ({
        url: `/v1/admin/outbound-notifications/${id}`,
        method: 'PUT',
        body: {
          name,
          setting_key,
          find_function,
          send_function,
          cadence,
        },
      }),
      invalidatesTags: [{ type: ADMIN_NOTIFICATIONS_TAG, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAllOutboundNotificationsQuery,
  useCreateOutboundNotificationMutation,
  useUpdateOutboundNotificationMutation,
} = adminNotificationConfigApi;
